import {FC, useState, useEffect, createContext, useContext} from 'react'
import {useQuery} from '@tanstack/react-query'
import {WithChildren} from '../../../../../../_metronic/helpers'
import {Job} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import { getJobs } from '../../_requests'

type QueryResponse = {
  data: Job[]
  paginationState: {
    page: number
    items_per_page: number
    total: number
    total_pages: number
    links?: Array<{label: string; active: boolean; url: string | null; page: number | null}>
  }
}

type QueryResponseContextType = {
  state: QueryResponse
  isLoading: boolean
  updateData: (data: Job[]) => void
  refetch: () => void
  query: string
}

const QueryResponseContext = createContext<QueryResponseContextType | undefined>(undefined)

const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state: queryRequest} = useQueryRequest()
  const [state, setState] = useState<QueryResponse>({
    data: [],
    paginationState: {
      page: 1,
      items_per_page: 20,
      total: 0,
      total_pages: 1,
      links: [],
    },
  })

  // Build query string with pagination parameters from queryRequest
  const currentPage = (queryRequest as any)?.page || 1
  const itemsPerPage = (queryRequest as any)?.items_per_page || 20
  const searchTerm = (queryRequest as any)?.search || ''
  const sortParam = (queryRequest as any)?.sort || ''
  const orderParam = (queryRequest as any)?.order || ''
  // const offset = (currentPage - 1) * itemsPerPage

  // If backend doesn't support search, we fetch a larger page to allow client-side filtering.

  const fetchItemsPerPage = searchTerm ? 1000 : itemsPerPage

  // Build query string with page, requested items_per_page and search parameter
  let queryString = `page=${currentPage}&items_per_page=${fetchItemsPerPage}`
  // if (searchTerm) {
  //   queryString += `&search=${encodeURIComponent(searchTerm)}`
  // }
  if (sortParam) {
    queryString += `&sort=${encodeURIComponent(sortParam)}`
  }
  if (orderParam) {
    queryString += `&order=${encodeURIComponent(orderParam)}`
  }
  if (searchTerm) {
    queryString += `&search=${encodeURIComponent(searchTerm)}`
  }

  // console.log('[Events] fetch query:', queryString)

  const {isFetching, refetch, data: apiData} = useQuery({
    queryKey: ['jobs-list', currentPage, fetchItemsPerPage, searchTerm, sortParam, orderParam],
    queryFn: () => getJobs(queryString),
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (apiData) {
      //    console.log('[Events] apiData:', apiData)
      const dataArray: Job[] = Array.isArray(apiData) ? apiData : apiData.data || []
      const pagination = apiData?.payload?.pagination

      if (searchTerm) {
        const lower = searchTerm.toLowerCase()
        const filteredAll = dataArray.filter((j) => {
          let locationsString = j.location || ''
          if (Array.isArray(j.locations)) {
            locationsString = j.locations.join(' ')
          }
          const combined =
            `${j.jobTitle || ''} ${j.companyName || ''} ${locationsString} ${j.description || ''}`.toLowerCase()
          return combined.includes(lower)
        })
        // paginate the filtered results according to the UI page size
        const totalFiltered = filteredAll.length
        const pageStart = (currentPage - 1) * itemsPerPage
        const paginated = filteredAll.slice(pageStart, pageStart + itemsPerPage)

        //    console.log('[Events] filteredAll.length, paginated.length:', totalFiltered, paginated.length)

        const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage))

        setState({
          data: paginated,
          paginationState: {
            page: currentPage,
            items_per_page: itemsPerPage,
            total: totalFiltered,
            total_pages: totalPages,
            links: pagination?.links || [],
          },
        })
      } else {
        setState({
          data: dataArray,
          paginationState: {
            page: pagination?.page || 1,
            items_per_page: pagination?.items_per_page || itemsPerPage,
            total: pagination?.total ?? dataArray.length,
            total_pages: pagination?.total_pages || 1,
            links: pagination?.links || [],
          },
        })
      }
    }
  }, [apiData, currentPage, itemsPerPage, searchTerm])

  const updateData = (data: Job[]) => {
    setState((prevState) => ({
      ...prevState,
      data,
    }))
  }

  return (
    <QueryResponseContext.Provider value={{state, isLoading: isFetching, updateData, refetch, query: queryString}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => {
  const context = useContext(QueryResponseContext)
  if (!context) {
    throw new Error('useQueryResponse must be used within QueryResponseProvider')
  }
  return context
}

const useQueryResponseData = () => {
  const context = useContext(QueryResponseContext)
  if (!context) {
    throw new Error('useQueryResponseData must be used within QueryResponseProvider')
  }
  return context.state.data
}

const useQueryResponseLoading = () => {
  const context = useContext(QueryResponseContext)
  if (!context) {
    throw new Error('useQueryResponseLoading must be used within QueryResponseProvider')
  }
  return context.isLoading
}

const useQueryResponsePagination = () => {
  const context = useContext(QueryResponseContext)
  if (!context) {
    throw new Error('useQueryResponsePagination must be used within QueryResponseProvider')
  }
  return context.state.paginationState
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponseLoading,
  useQueryResponsePagination,
}
